import {
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  Camera,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileSignature,
  FileText,
  Globe,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Menu,
  Network,
  Package,
  Receipt,
  Scale,
  Settings,
  Shield,
  Sparkles,
  TrendingUp,
  UserCircle,
  UserCheck,
  UserMinus,
  UserPlus,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { Suspense, useState, type ElementType, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';

import { env } from '../lib/env.js';

import { ImpersonationBanner } from './admin/ImpersonationBanner';
import { CommandPalette } from './CommandPalette';
import { LanguageToggle } from './LanguageToggle.js';
import { NotificationBell } from './NotificationBell';
import { TrialCountdownBanner } from './subscription/TrialCountdownBanner';

import { useAuth } from '@/contexts/AuthContext';
import { deriveAgencyNav } from '@/features/agency/agency-nav.logic';
import { isAwaitingAccess } from '@/features/auth/pending-access.logic';
import { useCommandPaletteShortcut } from '@/hooks/useCommandPaletteShortcut';
import { useCommandPaletteStore } from '@/stores/command-palette.store';

function Fallback(): ReactNode {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-sm text-slate-400">
      Loading…
    </div>
  );
}

// Role constants — used to gate nav groups
const HR_AND_ABOVE = ['tenant_admin', 'hr_admin', 'hr_viewer'];
const MANAGER_AND_ABOVE = ['tenant_admin', 'hr_admin', 'hr_viewer', 'manager'];
// Agency (Wave A1) has its own role set rather than reusing MANAGER_AND_ABOVE.
// The nav must match the CASL grants in server/src/shared/rbac/abilities.ts:
// `hr_admin` / `hr_viewer` hold NO Agency* grants (HR staff are not agency
// production staff), so showing them the tab would give a link where every
// call 403s. `employee` — the legacy in-house operator — DOES hold
// read/update on AgencyJob, and was previously shown no entry at all.
//
// Fix round 3, MINOR: `agency_owner`, `account_manager` and `designer`
// (M32a Task 22's three new agency roles) were missing here entirely, so
// none of them had a nav entry into the module the last three fix rounds
// were spent securing for exactly those roles. Added; per-tab visibility
// within the module (e.g. Intake, which `designer`/`employee` cannot use)
// is handled inside `AgencyPage.tsx`, not here — this array only gates
// whether the module is reachable at all.
/**
 * Icons for the agency shell's routes. The decision about WHICH routes a
 * person may reach lives in `deriveAgencyNav` (pure, tested); this map only
 * answers how each one is drawn, which is the shell's business.
 */
const AGENCY_NAV_ICONS: Record<string, ElementType> = {
  '/agency/clients': Building2,
  '/agency/my-clients': Users,
  '/agency/tasks': ClipboardList,
  '/agency/commitments': BarChart3,
  '/agency/settings': Settings,
  '/payroll': Wallet,
  '/leave': BookOpen,
  '/admin/roles': Shield,
};

const AGENCY_ROLES = [
  'tenant_admin',
  'manager',
  'employee',
  'agency_owner',
  'account_manager',
  'designer',
];
const ADMIN_ONLY = ['tenant_admin'];

interface NavItem {
  to: string;
  icon: ElementType;
  label: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
  /** If set, the group is hidden unless the user has at least one of these roles. */
  minRoles?: string[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'My Portal',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/ess', icon: UserCircle, label: 'My Portal' },
    ],
  },
  {
    title: 'Core HR',
    minRoles: MANAGER_AND_ABOVE,
    items: [
      { to: '/employees', icon: Users, label: 'Employees' },
      { to: '/org', icon: Network, label: 'Organisation' },
      { to: '/onboarding', icon: UserPlus, label: 'Onboarding' },
      { to: '/lifecycle', icon: UserCheck, label: 'Lifecycle' },
      { to: '/exit', icon: UserMinus, label: 'Exit Management' },
    ],
  },
  {
    title: 'Time & Pay',
    minRoles: MANAGER_AND_ABOVE,
    items: [
      { to: '/attendance', icon: ClipboardList, label: 'Attendance' },
      { to: '/leave', icon: BookOpen, label: 'Leave' },
      { to: '/payroll', icon: CreditCard, label: 'Payroll' },
      { to: '/expenses', icon: Receipt, label: 'Expenses' },
      { to: '/compensation', icon: Wallet, label: 'Compensation' },
    ],
  },
  {
    title: 'Talent',
    minRoles: MANAGER_AND_ABOVE,
    items: [
      { to: '/recruitment', icon: Briefcase, label: 'Recruitment' },
      { to: '/recruitment/careers-page', icon: Globe, label: 'Careers Page' },
      { to: '/performance', icon: TrendingUp, label: 'Performance' },
      { to: '/training', icon: BookOpen, label: 'Training' },
      { to: '/engagement', icon: BarChart3, label: 'Engagement' },
    ],
  },
  {
    title: 'Operations',
    minRoles: MANAGER_AND_ABOVE,
    items: [
      { to: '/assets', icon: Package, label: 'Assets' },
      { to: '/helpdesk', icon: HelpCircle, label: 'Helpdesk' },
      { to: '/documents', icon: FileText, label: 'Documents' },
      { to: '/contracts', icon: FileSignature, label: 'Contracts' },
      { to: '/vendors', icon: Globe, label: 'Vendors' },
    ],
  },
  {
    // Wave A1 — Agency Work Management (M28-M31). Line-of-business module for
    // the creative-agency vertical; gated like the other LOB groups.
    title: 'Agency',
    minRoles: AGENCY_ROLES,
    items: [{ to: '/agency', icon: Camera, label: 'Agency Work' }],
  },
  {
    title: 'Compliance',
    minRoles: HR_AND_ABOVE,
    items: [
      { to: '/compliance', icon: Scale, label: 'Compliance' },
      { to: '/whistleblower', icon: Shield, label: 'Whistleblower' },
      { to: '/dei', icon: Users, label: 'DEI' },
      { to: '/workforce', icon: Building2, label: 'Workforce' },
    ],
  },
  {
    title: 'Analytics',
    minRoles: HR_AND_ABOVE,
    items: [{ to: '/reports', icon: BarChart3, label: 'Reports' }],
  },
  {
    title: 'AI',
    items: [{ to: '/ai-copilot', icon: Sparkles, label: 'AI Copilot' }],
  },
  {
    title: 'Admin',
    minRoles: ADMIN_ONLY,
    items: [
      { to: '/subscription', icon: LayoutDashboard, label: 'Subscription' },
      { to: '/settings/company', icon: Settings, label: 'Company Settings' },
      { to: '/admin/roles', icon: Shield, label: 'User Roles' },
    ],
  },
  {
    title: 'Platform',
    minRoles: ['super_admin'],
    items: [{ to: '/admin', icon: Shield, label: 'Super Admin' }],
  },
];

function SidebarNav({ onClose }: { onClose?: () => void }): ReactNode {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout(): Promise<void> {
    await logout();
    void navigate('/login');
    onClose?.();
  }

  function toggle(title: string): void {
    setCollapsed((prev) => ({ ...prev, [title]: !prev[title] }));
  }

  // An agency tenant gets the agency product, not the HR one with an extra
  // link bolted on. `deriveAgencyNav` returns null for every non-agency
  // tenant, which is what leaves the other 27 modules untouched by this.
  const agencyGroups = deriveAgencyNav(user?.modules ?? [], user?.roles ?? []);
  // A self-registered account holds only `member` and can reach nothing. It
  // sees the "waiting for access" screen, so offering it a sidebar of links
  // that all 403 would contradict the message on the page.
  const awaitingAccess = isAwaitingAccess(user?.roles ?? []);
  const visibleGroups: NavGroup[] = awaitingAccess
    ? []
    : agencyGroups !== null
      ? agencyGroups.map((group) => ({
          title: group.label,
          items: group.items.map((item) => ({
            to: item.to,
            label: item.label,
            icon: AGENCY_NAV_ICONS[item.to] ?? Camera,
          })),
        }))
      : NAV_GROUPS.filter(
          (g) =>
            !g.minRoles ||
            (user?.roles ?? []).includes('super_admin') ||
            (user?.roles ?? []).includes('tenant_admin') ||
            g.minRoles.some((r) => (user?.roles ?? []).includes(r)),
        );

  return (
    <div className="flex h-full flex-col" style={{ background: 'hsl(var(--sidebar-bg))' }}>
      {/* Logo */}
      <div className="flex h-14 items-center justify-between border-b border-white/10 px-4">
        <Link to="/" className="text-base font-bold text-white" onClick={onClose}>
          {env.VITE_APP_NAME}
        </Link>
        {onClose && (
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {visibleGroups.map((group) => {
          const isCollapsed = collapsed[group.title];
          return (
            <div key={group.title} className="mb-1">
              <button
                onClick={() => toggle(group.title)}
                className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200"
              >
                <span>{group.title}</span>
                {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
              </button>
              {!isCollapsed && (
                <div className="mt-0.5 space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                            isActive
                              ? 'bg-blue-600 text-white font-medium'
                              : 'text-slate-300 hover:bg-white/10 hover:text-white'
                          }`
                        }
                      >
                        <Icon size={15} />
                        {item.label}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="border-t border-white/10 px-3 py-3">
        <div className="mb-2 truncate text-xs text-slate-400">{user?.email}</div>
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-300 hover:bg-white/10 hover:text-white"
        >
          <LogOut size={15} />
          Sign out
        </button>
      </div>
    </div>
  );
}

export function RootLayout(): ReactNode {
  useCommandPaletteShortcut();
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const openCommandPalette = useCommandPaletteStore((s) => s.open);

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Suspense fallback={<Fallback />}>
          <Outlet />
        </Suspense>
      </div>
    );
  }

  return (
    <>
      <ImpersonationBanner />
      <TrialCountdownBanner />
      <CommandPalette />
      <div className="flex min-h-screen bg-background">
        {/* Desktop sidebar */}
        <aside className="hidden w-56 shrink-0 md:block">
          <div className="fixed top-0 h-full w-56 overflow-hidden">
            <SidebarNav />
          </div>
        </aside>

        {/* Mobile sidebar overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 flex md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
            <div className="relative w-56 shrink-0">
              <SidebarNav onClose={() => setMobileOpen(false)} />
            </div>
          </div>
        )}

        {/* Main */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Mobile top bar */}
          <header className="flex h-14 items-center gap-3 border-b bg-white px-4 md:hidden">
            <button
              onClick={() => setMobileOpen(true)}
              className="rounded p-1 text-slate-500 hover:bg-slate-100"
            >
              <Menu size={20} />
            </button>
            <span className="font-semibold text-slate-800">{env.VITE_APP_NAME}</span>
            <div className="ml-auto">
              <NotificationBell />
            </div>
          </header>

          {/* Desktop top bar */}
          <header className="hidden h-12 items-center justify-end gap-3 border-b bg-white px-6 md:flex">
            <button
              type="button"
              onClick={openCommandPalette}
              className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Open command palette"
            >
              <span>Search</span>
              <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-600 shadow-sm">
                ⌘K
              </kbd>
            </button>
            <LanguageToggle />
            <NotificationBell />
          </header>

          <main className="flex-1 px-6 py-6">
            <Suspense fallback={<Fallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    </>
  );
}
